import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, BarChart3, Clock, PlusCircle, TrendingUp, TrendingDown, Swords, Share2, Users, QrCode, Lock, ShoppingBag, MapPin, ChevronDown } from "lucide-react";
import { XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart } from "recharts";
import api from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
import { money } from "../utils/currency";
import { RedemptionGoalCard } from "../components/dashboard/RedemptionGoalCard";
import { HonestFunnel, TrafficSources } from "../components/dashboard/HonestFunnel";
import { BoostReport } from "../components/dashboard/BoostReport";
import { RivalMap } from "../components/dashboard/RivalMap";
import { useEnabledCategoryNames } from "../hooks/useEnabledCategories";
import { catLabel } from "../utils/categoryLabel";
import { useLang } from "../context/LanguageContext";
import { thumb } from "../utils/image";

const Kpi = ({ icon: Icon, label, value, sub, tint, color, testId, subColor }) => (
  <div className="bg-card border border-border rounded-2xl p-5" data-testid={testId}>
    <span className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ background: tint }}>
      <Icon size={20} style={{ color }} />
    </span>
    <p className="text-2xl font-extrabold text-foreground mt-3">{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{label}</p>
    {sub ? <p className="text-xs font-semibold mt-1" style={{ color: subColor || color }}>{sub}</p> : null}
  </div>
);

const fmtDay = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
};

const Analytics = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [comp, setComp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("views");
  const [rival, setRival] = useState(null);
  const [rivalName, setRivalName] = useState(null);
  const [rivalLoading, setRivalLoading] = useState(false);
  const [radius, setRadius] = useState(5);
  const [openSpots, setOpenSpots] = useState(null);
  const [catFilter, setCatFilter] = useState(() => localStorage.getItem("lp-comp-cat") || "");
  const catNames = useEnabledCategoryNames();

  useEffect(() => {
    api.get("/my/analytics").then((r) => setA(r.data)).catch(() => setA(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (catFilter) localStorage.setItem("lp-comp-cat", catFilter);
    const params = {};
    if (radius) params.radiusKm = radius;
    if (catFilter) params.category = catFilter;
    api.get("/my/competition", { params })
      .then((r) => setComp(r.data)).catch(() => setComp(null));
  }, [radius, catFilter]);

  const openRival = (business) => {
    setRivalName(business);
    setRival(null);
    setRivalLoading(true);
    api.get("/my/competition/deals", { params: { business } })
      .then((r) => setRival(r.data))
      .catch(() => setRival(null))
      .finally(() => setRivalLoading(false));
  };

  const beatDeal = (d, rivalBusiness) => {
    const target = Math.min(90, (d.discount || 0) + 5);
    navigate("/post-deal", { state: { undercut: { title: `Beat ${rivalBusiness}: ${d.title}`, discount: target, rivalBusiness, rivalTitle: d.title, rivalDiscount: d.discount || 0 } } });
  };

  if (loading) return <div className="h-96 rounded-2xl bg-secondary animate-pulse" />;
  if (!a) return <p className="text-muted-foreground py-10">{t("an.couldNotLoadAnalytics")}</p>;

  const f = a.funnel || {};

  const METRICS = [
    ["views", t("an2.mViews"), a.viewsOverTime, "views", "#16a34a"],
    ["redemptions", t("an2.mRedemptions"), a.redemptionsOverTime, "redemptions", "#2563eb"],
    ["shares", t("an2.mShares"), a.sharesOverTime, "shares", "#0d9488"],
    ["followers", t("an2.mFollowers"), a.followerGrowth, "followers", "#b8860b"],
  ];
  const active = METRICS.find(([k]) => k === metric) || METRICS[0];
  const series = (active[2] || []).map((p) => ({ ...p, label: fmtDay(p.date) }));
  const hasSeries = series.some((p) => (p[active[3]] || 0) > 0);

  // One table instead of four leaderboards: views, grabs, redeemed, rate and shares per deal.
  const redById = Object.fromEntries((a.redemptionsByDeal || []).map((d) => [d.dealId, d]));
  const shareById = Object.fromEntries((a.mostShared || []).map((d) => [d.id, d]));
  const rows = (a.topDeals || []).map((d) => ({
    ...d,
    issued: redById[d.id]?.issued ?? d.claims ?? 0,
    redeemed: redById[d.id]?.redeemed ?? 0,
    rate: redById[d.id]?.rate ?? null,
    shares: shareById[d.id]?.shares ?? 0,
  }));

  return (
    <div className="space-y-6 lp-fade-up">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground flex items-center gap-2"><BarChart3 className="text-[#16a34a]" /> Analytics</h1>
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            {t("an.performanceOfYourDeals")}
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-foreground bg-secondary rounded-full px-2.5 py-0.5" data-testid="stat-total-followers">
              <Users size={12} className="text-[#16a34a]" /> {t("an2.followersChip", { n: a.totalFollowers ?? 0 })}
            </span>
          </p>
        </div>
        <Link to="/post-deal" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-5 py-2.5 rounded-xl transition-colors"><PlusCircle size={18} /> {t("an.postADeal")}</Link>
      </div>

      {a.totalDeals === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-12 text-center">
          <p className="text-muted-foreground mb-4">{t("an.noDealsYetPost")}</p>
          <Link to="/post-deal" className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white font-semibold px-6 py-2.5 rounded-xl"><PlusCircle size={18} /> {t("an.postADeal")}</Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi testId="stat-total-views" icon={Eye} label={t("an.totalViews")} value={(a.totalViews || 0).toLocaleString()}
              sub={a.viewsWoW != null ? t("an2.subVsLastWeek", { n: `${a.viewsWoW >= 0 ? "+" : ""}${a.viewsWoW}` }) : null}
              subColor={a.viewsWoW >= 0 ? "#16a34a" : "#e11d48"} tint="#e6effd" color="#2563eb" />
            <Kpi testId="stat-total-grabs" icon={ShoppingBag} label={t("an2.grabs")} value={(f.grabs || 0).toLocaleString()}
              sub={t("an2.subOfViews", { n: f.grabRate || 0 })} tint="#e7f6ec" color="#16a34a" />
            <Kpi testId="stat-total-redemptions" icon={QrCode} label={t("an.inStoreRedemptions")} value={(a.totalRedemptions || 0).toLocaleString()}
              sub={t("an2.subOfGrabs", { n: f.redeemRate || 0 })} tint="#e6effd" color="#2563eb" />
            <Kpi testId="stat-near-expiry" icon={Clock} label={t("an.nearExpiry")} value={a.nearExpiryDeals}
              sub={t("an2.subExpired", { n: a.expiredDeals })} subColor="#64748b" tint="#fde8ee" color="#e11d48" />
          </div>

          {a.limited ? (
            <div className="bg-card border border-border rounded-2xl p-6" data-testid="analytics-locked">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1"><Lock size={16} className="text-[#b8860b]" /> {t("an.lockedTitle")}</div>
              <p className="text-sm text-muted-foreground">{(a.lockedFeatures || []).slice(0, 3).join(" · ")}</p>
              <div className="mt-4 h-40 rounded-xl bg-gradient-to-b from-[#16a34a]/15 to-transparent blur-[3px] grid place-items-center text-xs text-muted-foreground">
                {t("an2.mViews")}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Link to="/pricing" data-testid="analytics-upgrade-btn"
                  className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#0f2a1a] hover:bg-[#16341f] text-white px-3 py-2 rounded-xl transition-colors">
                  {t("an.lockedCta")}
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-card border border-border rounded-2xl p-6" data-testid="trend-chart">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <h3 className="font-bold text-foreground flex items-center gap-2">
                  {a.viewsWoW >= 0 ? <TrendingUp size={18} className="text-[#16a34a]" /> : <TrendingDown size={18} className="text-rose-500" />} {t("an2.trend")}
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {METRICS.map(([key, label]) => (
                    <button key={key} onClick={() => setMetric(key)} data-testid={`trend-metric-${key}`}
                      className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${metric === key
                        ? "bg-[#0f2a1a] text-white border-[#0f2a1a]"
                        : "bg-card text-muted-foreground border-border hover:border-[#16a34a]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {!hasSeries ? (
                <p className="text-sm text-muted-foreground py-10 text-center" data-testid="trend-empty">{t("an2.noTrendData")}</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={series} margin={{ top: 5, right: 10, left: -8, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={active[4]} stopOpacity={0.28} />
                        <stop offset="100%" stopColor={active[4]} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={18} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} width={52} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid var(--border)", background: "var(--card)", fontSize: 12 }} labelStyle={{ fontWeight: 700 }} formatter={(v) => [`${v} ${active[1].toLowerCase()}`, ""]} />
                    <Area type="monotone" dataKey={active[3]} stroke={active[4]} strokeWidth={2.5} fill="url(#trendFill)" dot={false} activeDot={{ r: 4 }} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              <p className="text-xs text-muted-foreground mt-3" data-testid="best-time-note">
                {a.bestHour != null
                  ? `${t("an.bestTimeToPost")}: ${String(a.bestHour).padStart(2, "0")}:00${a.bestWeekday != null ? ` · ${WEEKDAYS[a.bestWeekday]}` : ""} · ${t("an.lastDays")}`
                  : t("an.lastDays")}
              </p>
            </div>
          )}

          {rows.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-6" data-testid="deal-performance">
              <h3 className="font-bold text-foreground mb-4">{t("an2.dealTable")}</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-muted-foreground border-b border-border">
                      <th className="py-2 font-medium">{t("an2.colDeal")}</th>
                      <th className="py-2 font-medium text-right">{t("an2.colViews")}</th>
                      <th className="py-2 font-medium text-right">{t("an2.colGrabs")}</th>
                      {!a.limited && <th className="py-2 font-medium text-right">{t("an2.colRedeemed")}</th>}
                      {!a.limited && <th className="py-2 font-medium text-right">{t("an2.colRate")}</th>}
                      {!a.limited && <th className="py-2 font-medium text-right">{t("an2.colShares")}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d, i) => (
                      <tr key={d.id} className="border-b border-border/60 last:border-0" data-testid={`deal-row-${i}`}>
                        <td className="py-2.5">
                          <Link to={`/deal/${d.id}`} className="flex items-center gap-2.5 min-w-0 group">
                            {d.image
                              ? <img src={thumb(d.image, 96)} alt={d.title} loading="lazy" decoding="async" className="h-9 w-9 rounded-lg object-cover shrink-0" />
                              : <span className="h-9 w-9 rounded-lg bg-secondary shrink-0" />}
                            <span className="min-w-0">
                              <span className="block font-semibold text-foreground truncate group-hover:text-[#16a34a] max-w-[16rem]">{d.title}</span>
                              <span className="block text-xs text-muted-foreground">{d.discount}% off</span>
                            </span>
                          </Link>
                        </td>
                        <td className="py-2.5 text-right text-foreground">{(d.views || 0).toLocaleString()}</td>
                        <td className="py-2.5 text-right text-foreground">{d.issued || 0}</td>
                        {!a.limited && <td className="py-2.5 text-right text-foreground">{d.redeemed || 0}</td>}
                        {!a.limited && (
                        <td className={`py-2.5 text-right font-bold ${d.rate == null ? "text-muted-foreground" : d.rate >= 50 ? "text-[#16a34a]" : "text-[#b8860b]"}`}>
                          {d.rate == null ? "—" : `${d.rate}%`}
                        </td>
                        )}
                        {!a.limited && <td className="py-2.5 text-right text-muted-foreground">{d.shares || 0}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">{a.limited ? t("an2.tableNoteFree") : t("an2.tableNote")}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <HonestFunnel funnel={a.funnel} directions={a.totalDirections} />
            <TrafficSources />
          </div>

          <RedemptionGoalCard />
          <BoostReport />

          {comp && (
            <div className="bg-card border border-border rounded-2xl p-6" data-testid="competition-panel">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                <h3 className="font-bold text-foreground flex items-center gap-2"><Swords size={18} className="text-[#16a34a]" /> {t("an.yourCompetition")}
                  <select value={comp.category || ""} onChange={(e) => setCatFilter(e.target.value)}
                    data-testid="competition-category"
                    className="ml-1 text-sm font-semibold bg-secondary border border-border rounded-xl px-2.5 py-1.5 text-foreground outline-none focus:border-[#16a34a]">
                    {[comp.myCategory, ...catNames.filter((c) => c !== comp.myCategory)].filter(Boolean).map((c) => (
                      <option key={c} value={c}>{catLabel(c, t)}{c === comp.myCategory ? ` · ${t("an3.myCategory")}` : ""}</option>
                    ))}
                  </select>
                </h3>
                <div className="flex items-center gap-2 text-xs">
                  <span className="bg-secondary px-3 py-1.5 rounded-full font-semibold text-foreground">Your avg discount: <span className="text-[#16a34a]">{comp.myAvgDiscount}%</span></span>
                  <span className="bg-secondary px-3 py-1.5 rounded-full font-semibold text-foreground">Category avg: <span className="text-[#2563eb]">{comp.categoryAvgDiscount}%</span></span>
                </div>
              </div>
              <div className="mb-4" data-testid="competition-radius">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-semibold text-muted-foreground mr-1 flex items-center gap-1"><MapPin size={12} className="text-[#16a34a]" /> {t("an3.withinRadius")}</span>
                  {[1, 3, 5, 10, 25, 0].map((km) => (
                    <button key={km} onClick={() => setRadius(km)} disabled={comp.radiusLocked || !comp.hasLocation}
                      data-testid={`radius-${km || "any"}`}
                      className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${radius === km
                        ? "bg-[#16a34a] border-[#16a34a] text-white"
                        : "border-border text-muted-foreground hover:border-[#16a34a] hover:text-[#16a34a]"}`}>
                      {km ? `${km} km` : t("an3.anywhere")}
                    </button>
                  ))}
                </div>
                {comp.radiusLocked ? (
                  <p className="text-xs text-muted-foreground mt-2 flex flex-wrap items-center gap-1.5" data-testid="radius-locked">
                    <Lock size={12} className="text-[#b8860b]" /> {t("an3.radiusLocked")}
                    <Link to="/pricing" className="font-bold text-[#16a34a] hover:underline" data-testid="radius-upgrade-link">{t("an.lockedCta")}</Link>
                  </p>
                ) : !comp.hasLocation ? (
                  <p className="text-xs text-muted-foreground mt-2 flex flex-wrap items-center gap-1.5" data-testid="radius-no-pin">
                    {t("an3.radiusNoPin")}
                    <Link to="/account" className="font-bold text-[#16a34a] hover:underline">{t("an3.pinMyShop")}</Link>
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground mt-2" data-testid="radius-summary">
                    {comp.radiusKm
                      ? t("an3.radiusSummary", { n: comp.competitorCount, km: comp.radiusKm, cat: (comp.category || "").toLowerCase() })
                      : t("an3.radiusSummaryAny", { n: comp.competitorCount, cat: (comp.category || "").toLowerCase() })}
                  </p>
                )}
              </div>
              {!comp.radiusLocked && comp.hasLocation && comp.competitors.length > 0 && (
                <div className="mb-4">
                  <RivalMap myLat={comp.myLat} myLng={comp.myLng} rivals={comp.competitors} radiusKm={comp.radiusKm} onCompare={openRival} />
                </div>
              )}
              {comp.competitorsHidden ? (
                <div className="rounded-xl bg-secondary p-4 text-center" data-testid="competition-hidden">
                  <p className="text-sm font-bold text-foreground">{t("an3.rivalsHidden", { n: comp.competitorCount })}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("an3.rivalsHiddenNote")}</p>
                  <Link to="/pricing" data-testid="competition-upgrade-btn"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold bg-[#0f2a1a] hover:bg-[#16341f] text-white px-3 py-2 rounded-xl transition-colors">
                    {t("an.lockedCta")}
                  </Link>
                </div>
              ) : comp.competitors.length === 0 ? (                <p className="text-sm text-muted-foreground py-6 text-center" data-testid="competition-empty">
                  {comp.radiusKm
                    ? t("an3.noneInRadius", { km: comp.radiusKm, cat: (comp.category || "").toLowerCase() })
                    : t("an3.noneAnywhere", { cat: (comp.category || "").toLowerCase() })}
                </p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground mb-3">
                    {comp.myAvgDiscount >= comp.categoryAvgDiscount
                      ? `Your average discount beats the category average — you're competitive across ${comp.competitorCount} rival${comp.competitorCount === 1 ? "" : "s"}.`
                      : `Rivals are discounting ~${(comp.categoryAvgDiscount - comp.myAvgDiscount).toFixed(1)}% deeper on average. Consider a sharper offer to stand out.`}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="competitor-cards">
                    {comp.competitors.map((c, i) => {
                      const deeper = c.avgDiscount > comp.myAvgDiscount;
                      const open = openSpots === i;
                      return (
                        <div key={i} data-testid={`competitor-card-${i}`}
                          className="group relative rounded-2xl border border-border bg-gradient-to-b from-card to-secondary/40 p-4 hover:border-[#16a34a]/60 hover:shadow-lg hover:shadow-[#16a34a]/5 transition-all">
                          <div className="flex items-start justify-between gap-3">
                            <button onClick={() => openRival(c.business)} data-testid={`competitor-open-${i}`}
                              className="min-w-0 text-left">
                              <p className="font-extrabold text-foreground truncate group-hover:text-[#16a34a] transition-colors">{c.business}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {[c.location, c.country].filter((x, idx, arr) => x && arr.indexOf(x) === idx
                                  && !(idx === 1 && (c.location || "").includes(x))).join(", ")}
                              </p>
                            </button>
                            <span className={`shrink-0 rounded-xl px-2.5 py-1.5 text-sm font-extrabold ${deeper
                              ? "bg-[#e11d48]/10 text-[#e11d48]" : "bg-[#16a34a]/10 text-[#16a34a]"}`}
                              data-testid={`competitor-discount-${i}`}>{c.avgDiscount}%</span>
                          </div>
                          <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
                            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-muted-foreground">
                              <MapPin size={11} className="text-[#16a34a]" />
                              {c.distanceKm == null ? "—"
                                : c.distanceKm === 0 ? t("an3.sameSpot")
                                : c.distanceKm < 0.2 ? t("an3.underTenth")
                                : t("an3.kmAway", { km: c.distanceKm })}
                            </span>
                            <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-muted-foreground">
                              {t("an3.liveCount", { n: c.activeDeals })}
                            </span>
                            {c.locations > 1 && (
                              <button onClick={() => setOpenSpots(open ? null : i)} data-testid={`competitor-locations-${i}`}
                                className="inline-flex items-center gap-1 rounded-full bg-[#16a34a]/10 px-2.5 py-1 font-bold text-[#16a34a] hover:bg-[#16a34a]/20 transition-colors">
                                {t("an3.locationsCount", { n: c.locations })}
                                <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                              </button>
                            )}
                          </div>
                          {open && (
                            <ul className="mt-2.5 space-y-1 border-t border-border pt-2.5" data-testid={`competitor-spots-${i}`}>
                              {(c.spots || []).map((sp, j) => (
                                <li key={j} className="flex items-center justify-between gap-2 text-xs">
                                  <span className="text-muted-foreground">{t("an3.locationN", { n: j + 1 })}</span>
                                  <span className="font-bold text-foreground">
                                    {sp.distanceKm == null ? "—"
                                      : sp.distanceKm === 0 ? t("an3.sameSpot")
                                      : t("an3.kmAway", { km: sp.distanceKm })}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">{t("an.tipClickARival")}</p>
                </>
              )}
            </div>
          )}
        </>
      )}

      <Dialog open={!!rivalName} onOpenChange={(o) => { if (!o) { setRivalName(null); setRival(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="competition-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Swords size={18} className="text-[#16a34a]" /> You vs {rivalName}</DialogTitle>
          </DialogHeader>
          {rivalLoading || !rival ? (
            <div className="h-40 flex items-center justify-center text-muted-foreground">{t("an.loadingComparison")}</div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {[{ side: rival.me, mine: true }, { side: rival.rival, mine: false }].map(({ side, mine }) => (
                <div key={mine ? "me" : "rival"} className="space-y-3" data-testid={mine ? "compare-me" : "compare-rival"}>
                  <div className={`rounded-xl p-3 text-center ${mine ? "bg-[#e7f6ec] dark:bg-[#16341f]" : "bg-secondary"}`}>
                    <p className="text-sm font-bold text-foreground truncate">{mine ? "You" : side.business}</p>
                    <p className="text-xs text-muted-foreground">{side.activeDeals} live deal{side.activeDeals === 1 ? "" : "s"} · avg {side.avgDiscount}% off</p>
                  </div>
                  {side.deals.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-6">{t("an.noLiveDeals")}</p>
                  ) : side.deals.map((d, i) => (
                    <div key={d.id || i} className="flex gap-3 items-center bg-card border border-border rounded-xl p-2.5" data-testid={`${mine ? "my" : "rival"}-deal-${i}`}>
                      {d.image && <img src={thumb(d.image, 96)} alt={d.title} loading="lazy" decoding="async" className="h-12 w-12 rounded-lg object-cover shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground truncate">{d.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {d.price != null ? <span className="font-bold text-[#16a34a]">{money(d.price, d.currency)}</span> : null}
                          {d.was != null ? <span className="line-through ml-1">{money(d.was, d.currency)}</span> : null}
                          {d.branchCount > 1 ? ` · ${d.branchCount} locations` : ""}
                        </p>
                      </div>
                      <span className="shrink-0 text-xs font-bold text-white bg-[#16a34a] rounded-full px-2 py-0.5">{d.discount}%</span>
                      {!mine && (
                        <button onClick={() => beatDeal(d, side.business)} data-testid={`beat-deal-${i}`} className="shrink-0 text-[11px] font-bold text-white bg-[#e11d48] hover:bg-[#be123c] rounded-full px-2.5 py-1 transition-colors">{t("an.matchOrBeat")}</button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {rival && !rivalLoading && (
            <p className="text-sm text-center text-muted-foreground mt-2" data-testid="compare-verdict">
              {rival.me.avgDiscount >= rival.rival.avgDiscount
                ? `Your deals average ${(rival.me.avgDiscount - rival.rival.avgDiscount).toFixed(1)}% deeper discounts than ${rivalName}. Nicely done! 🎯`
                : `${rivalName} is discounting ${(rival.rival.avgDiscount - rival.me.avgDiscount).toFixed(1)}% deeper on average — a sharper offer could win more shoppers.`}
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Analytics;
